import {
  Button,
  Chip,
  List,
  Typography,
  Zoom,
} from '@mui/material';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import { ExpertSuggestion } from 'FRONTEND/components/Calendar/AppointmentForm/ExpertSuggestion';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import Loader from 'FRONTEND/components/Loader';
import { pickAdditionalExpert, resetAdditionalExpertList, unpickAdditionalExpert } from 'FRONTEND/reducers/bookingSlice';
import {
  searchRequest as expertSearchRequest,
} from 'FRONTEND/reducers/expertSlice';
import {
  selectAdditionalSelectedExperts,
  selectUnselectedMatchingExperts,
} from 'FRONTEND/selectors/expertSelectors';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { getDurationSettingKey } from 'FRONTEND/utils/stringFormatting';

export const MultipleAvailableExpertStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const additionalSelectedExpertList = useSelector(selectAdditionalSelectedExperts);
  const matchingExpertList = useSelector(selectUnselectedMatchingExperts);
  const selectedType = useSelector((state) => state.booking.selectedType);
  const isLoading = useSelector((state) => state.app.requestCounter[expertSearchRequest.toString()] > 0);
  const isSmall = useSelector((state) => state.app.isSmall);

  useEffect(() => {
    if (shouldReset) {
      dispatch(resetAdditionalExpertList({ id: null }));
    }
  }, [shouldReset]);

  const handleExpertClick = (id) => {
    dispatch(pickAdditionalExpert({ id }));
  };

  const handleUnpickExpert = (id) => {
    dispatch(unpickAdditionalExpert({ id }));
  };

  const handleConfirmClick = () => {
    onStepCompleted();
  };

  const listClasses = cx('appointmentForm-resultList', { 'appointmentForm-resultList_small': isSmall });

  const additionalExpertAliases = additionalSelectedExpertList.map((expert) => expert?.alias);
  return (
    <StepTemplate
      label={i18n._('Select practitioners')}
      isLocked={isLocked}
      onEditClick={() => onStepClick()}
      isStepCompleted={additionalSelectedExpertList.length > 0 && isCompleted}
      editText={additionalExpertAliases.join(', ')}
      {...otherProps}
    >
      {additionalSelectedExpertList.length > 0 && (
        <div className='appointmentForm-line'>
          <TransitionGroup component={null}>
            {additionalSelectedExpertList.map((selectedExpert) => (
              <Zoom key={selectedExpert.id}>
                <div className='appointmentForm-horizontalListItem'>
                  <Chip
                    variant='outlined'
                    color='primary'
                    onDelete={() => handleUnpickExpert(selectedExpert.id)}
                    label={selectedExpert.alias || ''}
                    className='appointmentForm-horizontalListItem appointmentForm-noWrapItem'
                  />
                </div>
              </Zoom>
            ))}
          </TransitionGroup>
        </div>
      )}
      <Loader isLoading={isLoading} position='verticalFlow' />
      {matchingExpertList.length ? (
        <List dense className={listClasses}>
          {matchingExpertList.map((matchingExpert) => (
            <ExpertSuggestion
              key={matchingExpert.id}
              onClick={() => handleExpertClick(matchingExpert.id)}
              specialty={matchingExpert.primary_specialty}
              displayName={matchingExpert.alias}
              initials={getInitials(matchingExpert.alias)}
              picture={matchingExpert.picture}
              email={matchingExpert.login}
              duration={matchingExpert[getDurationSettingKey(selectedType)]}
              className='appointmentForm-expertSuggestion'
            />
          ))}
        </List>
      ) : (
        <Typography>{i18n._('No other expert is available for this slot or this type of appointment.')}</Typography>
      )}
      <div className='appointmentForm-line'>
        <Button
          variant='contained'
          onClick={handleConfirmClick}
          disabled={additionalSelectedExpertList.length === 0}
        >
          {i18n._('Confirm')}
        </Button>
      </div>
    </StepTemplate>
  );
};
